import _debug from 'debug'
import { stringify as safeStableStringify } from 'safe-stable-stringify'

import { _LevelupDB } from './_LevelupDB'
import API from "./API"
import { fetchBlob2, IReportProgress } from './API2'
import { BLOB_BLOCK_SIZE, VideoCache } from './VideoCache'
import { Root } from './Root'
import { detectDatabaseError, sendBeacon, systemError } from '../components/utils/Errors'
import { getAuthorizedStorageProjects, getHasElectronContext, isSlttAppStorageEnabled, retrieveBlob, retrieveBlobInfo, storeBlob, updateBlobUploadedStatus } from './SlttAppStorage'
import { convertFromCanonicalTimestamp } from './utils/dateServices'
import { serializeBlob, deserializeBlob } from './utils/serializeBlob'
import { shuffle } from 'underscore'
import { fmt } from '../components/utils/Fmt'
import { HOTFIX_UNBLOCK_CACHE_CLEAN_RECENT_BLOBS } from './VideoCacheHotFix'
import { doOnceWhenBackOnline } from '../components/utils/ServiceStatus'

const log = _debug('sltt:VideoCacheRecord')
const debug = _debug('slttdbg:VideoCacheRecord')
const intest = (localStorage.getItem('intest') === 'true')

type PropertiesOnly<T> = {
    [K in keyof T as T[K] extends (...args: any[]) => any ? never : K]: T[K]
}

/** can be used for accessDate (until user touches video). See see VideoCacheRecord.isInheritedUpload */
export const INHERITED_UPLOAD_ACCESS_DATE = '2018/09/11 00:00:00.000Z' // convertToCanonicalDateTimeFormat(new Date('2018-09-11T00:00:00.000Z'))

export enum VideoDownloadResult {
    SUCCESS = 'success', // Download of blob worked 
    MISSING = 'missing', // Blob has not been uploaded yet
    ERROR = 'error', // Retriable error
    FAILED = 'failed', // Permanent error 
    NONE = '', // Have not tried to download blob yet
}

/*
 * Cache signedUrl so that we do not have to repeatedly fetch them from backend when doing
 * error or missing retries.
 */
interface ISignedUrlEntry {
    signedUrl: string
    createdAt: number // timestamp in ms
}

export class SignedUrlFactory {
    signedUrlMap = new Map<string, ISignedUrlEntry>()

    static MAX_AGE = 20 * 60 * 60 * 1000 // 20 hours
    
    // if url created for this check in the last 20 hours, return it
    // otherwise fetch a new signed URL.
    // Returns [error, signedUrl]
    // Does not throw.
    async getSignedUrl(projectName: string, url: string, maxAgeOverride = 0): Promise<[Error | null, string]> {
        const { signedUrlMap } = this
        const maxAge  = maxAgeOverride || SignedUrlFactory.MAX_AGE

        let signedUrlEntry = signedUrlMap.get(url)
        const urlExpired = Date.now() - (signedUrlEntry?.createdAt ?? 0) > maxAge
        if (signedUrlEntry && !urlExpired) {
            maxAgeOverride && log('getSignedUrl cache HIT', fmt({url}))
            return [null, signedUrlEntry.signedUrl]
        }

        maxAgeOverride && log(`getSignedUrl cache MISS`, fmt({url, signedUrlEntry, urlExpired}))

        const [error, _signedUrl] = await API._getUrl(url)
        if (error) return [error, '']
        signedUrlMap.set(url, { signedUrl: _signedUrl, createdAt: Date.now() })

        return [null, _signedUrl]
    }
}

/*
 * A VideoBlob is a video file or a section of a video file.
 * Sections are uploaded individually to S3 then concatenated to reform original file.
 * Example id: TESTnm/210202_183235/211208_164428/220119_201027-5
 *     The 5th blob of a video created on Jan 19, 2022
 * 
 * We store these in a separate table from the video cache records because the cache
 * records are small and can ge kept in memory, but these records are videos and are large.
 */

export class VideoBlob {
    constructor(public _id: string, public blob?: Blob) {
        if (!this._id) throw Error('No _id')
    }

    async saveToDB() {
        if (!this.blob) throw Error('No blob to save')
        try {
            if (VideoCache.simulateSaveToDBError) throw Error(VideoCache.simulateSaveToDBError)
            const blobString = await serializeBlob(this.blob)
            // This regeneratedBlob may be overkill for all contexts (e.g. downloading), but...
            // the `Data Error: Failed to write blobs (Invalid Blob)` error will happen in browsers with the latest versions of 
            // Chromium (129.0.6668.71) (see https://issues.chromium.org/issues/369670458, and see https://github.com/ubsicap/sltt/issues/982)
            // As far as I (EricP) can this only happens in the context of attempting to cache (4 MB) blob chunks 
            // which were sliced from a file bound to an input element (e.g. from a file upload dialog or drop-and-drop target).
            // It does NOT happen when the same blobs are downloaded from s3.
            // TODO: research whether it's sufficient to regenerate the blob for `vcr.addBlob(blob)` in `copyMultipartFileToVideoCache()`
            // For now, we'll always regenerate the blob to make sure we catch all possible paths with the hope that this
            // will not cause a noticeable performance issue for downloading.
            const regeneratedBlob = deserializeBlob(blobString)
            // log(`blobString (${this._id})`, blobString)
            await VideoCache._db.put(VideoCache.VIDEOBLOBS, { _id: this._id, blob: regeneratedBlob })
        } catch (error) {
            detectDatabaseError(error)
            throw Error(`saveToDB ${this._id} ${error}`)
        }
        log(`saveToDB ${this._id}`)
    }

    async loadFromDB(mustBePresent?: boolean) {
        let doc = await VideoCache._db.get(VideoCache.VIDEOBLOBS, this._id)
        if (!doc || !doc.blob) {
            if (mustBePresent) {
                debugger
                throw Error(`not present in VIDEOBLOBS ${this._id}`)
            }
            this.blob = undefined
            return
        }

        this.blob = doc.blob
    }
}

/*
 *  We keep a record for each locally cached video.
 */
export class VideoCacheRecord {
    static systemError = (err: any) => { throw Error(err) }
    static _count = 0

    public _id = ''  // e.g. TESTnm/210202_183235/211208_164428/220119_201027-5
    public size = 0  // number of bytes in this video

    /** When this array is non-empty, the video has been broken into segments.
     * videBlobId(i) names the segments starting with index 0.
     * The i'th entry is true iff the corresponding segment blob has been uploaded. */
    public uploadeds: boolean[] = []
    /**
     * map vcrId to boolean array of blob statuses that are true if known to exist in last connected local team storage
     * Keep in sync using setSharedLanBlob()
     * Currently not persisted in the vcr
     **/
    static sharedLanBlobs: Map<string, boolean[]> = new Map()

    /*
     * When a video has been downloaded locally the following two arrays will be non empty.
     * There will be one entry for each Blob.
     */
    public downloadeds: boolean[] = []
    public downloadResults: VideoDownloadResult[] = []

    static forcedDownloadResult = (localStorage.getItem('forcedDownloadResult') ?? '') as VideoDownloadResult

    static signedUrlFactory = new SignedUrlFactory()

    // Show what has been uploaded so far.
    // If the video is being uploaded from another computer we don't know for sure.
    public get uploadedBlobs() {
        // uploadeds is non-empty iff video uploaded from current computer,
        // we know what is uploaded so far
        if (this.uploadeds.length > 0) return [...this.uploadeds]

        // Video was uploaded from another computer, start by guessing everything uploaded
        let uploadeds = Array(this.downloadeds.length).fill(true)

        // If no record is currently showing with a 403 error assume (for now)
        // that everything has been successfully uploaded at the other end
        let i = this.downloadResults.indexOf(VideoDownloadResult.MISSING)
        if (i < 0) return uploadeds

        // We found a 403, assume everything from that point on has not been uploaded yet
        uploadeds.fill(false, i, uploadeds.length)
        return uploadeds
    }

    public downloadMessage = ''

    public accessDate = ''

    public uploadStartTimeMs = -1
    public uploadFinishTimeMs = -1

    // If the download/upload for this video failed, the error reason is here.
    public error = ''

    private static cache = new Map<string, VideoCacheRecord>()

    static async getAllVcrsFromCache() {
        return Array.from(VideoCacheRecord.cache.values())
    }

    static updateCache(vcr: VideoCacheRecord) {
        let { cache } = VideoCacheRecord
        const { _id, uploadeds, downloadeds, size } = vcr

        function nonEmpty(states: boolean[]) { return states.length > 0 && states.some(x=>x) }

        if ((nonEmpty(downloadeds) || nonEmpty(uploadeds)) && size === 0) {
            // size is set incrementally by upload/download process so this should never happen
        }

        if (!_id) {
            debugger
        }

        cache.set(vcr._id, vcr)
    }

    /** for tests */
    static resetCache() {
        let { cache } = VideoCacheRecord
        cache.clear()
    }

    // Return vcr if present in cache
    static _get(_id: string) {
        let { cache } = VideoCacheRecord
        return cache.get(_id)
    }

    /**
     * If vcr with this _id present in cache, return it.
     * Otherwise create a new empty vcr with this id, add it to the cache, return it.
     */
    static async get(_id: string) {
        let { cache } = VideoCacheRecord
        let vcr = cache.get(_id)
        if (vcr) return vcr

        /* Sigh, there was a bad error for 2 months starting mid June 2020
        * when creating url fields for PassaageNoteItem.
        * the project name, which is the first thing in the url, was left empty.
        * Add a check to see if the video was cached under the no project name url.
        * This avoids needing to redownload the video which may be slow.
        */

        let parts = _id.split('/')
        let _idNoProjectName = '/' + parts.slice(1).join('/')
        vcr = cache.get(_idNoProjectName)
        if (vcr) { 
            log('cache HIT _idNoProjectName')
            return vcr 
        }

        // No cache entry for this video, create one
        vcr = new VideoCacheRecord({_id})
        cache.set(_id, vcr)
        return vcr
    }

    // Normally you should not use this.
    // Use static get() above so there is a cached copy
    constructor(doc: { _id: string }) {
        if (!doc._id) throw Error('No _id') // must at least have _id
        this.downloadResults = new Array(this.seqNum(doc._id)).fill(VideoDownloadResult.NONE)
        
        this.clone(doc)
    }

    dbg() {
        let { _id, uploadeds, downloadeds, error } = this
        return { _id, uploadeds, downloadeds, error }
    }

    // WARNING if new attributes added to this class, they MUST be added here
    // or constructor will not work correctly.
    private clone(doc: any) {
        this._id = doc._id
        this.size = doc.size || 0
        // this.blobsCount = doc.blobsCount || 0
        this.uploadeds = doc.uploadeds || []
        this.downloadeds = doc.downloadeds || []
        this.accessDate = doc.accessDate || ''
        this.error = doc.error || ''
        this.downloadMessage = doc.downloadMessage || ''

        this.uploadStartTimeMs = doc.uploadStartTimeMs || -1
        this.uploadFinishTimeMs = doc.uploadFinishTimeMs || -1
    }

    
    get downloadProgress() {
        if (this.downloadeds.length === 0) return 100

        let downloadedBlobs = (this.downloadeds.filter(x => x)).length
        return Math.floor((100 * downloadedBlobs / this.downloadeds.length) + 0.5)
    }

    // The video data is present in cache if download is complete OR
    // it was generated locally
    get hasBeenCached() {
        return this.downloaded /* generated remotely and downloaded */ ||
            this.uploadeds.length > 0 /* generated locally so started out in cache */
    }

    /** NOTE: doesn't persist with saveToDB */
    setSharedLanBlob(blobId: string, isShared: boolean) {
        if (!getHasElectronContext()) return
        const iBlob = this.seqNum(blobId) - 1
        this.checkBlobIndex(iBlob)
        let blobLanShareds = VideoCacheRecord.sharedLanBlobs.get(this._id)
        if (blobLanShareds) {
            if (blobLanShareds.length > 0) {
                const originalValue = blobLanShareds[iBlob] ?? false
                if (originalValue === isShared) return
            }
        } else {
            blobLanShareds = new Array(this.totalBlobs).fill(false)
            VideoCacheRecord.sharedLanBlobs.set(this._id, blobLanShareds)
        }
        blobLanShareds[iBlob] = isShared
    }

    /**
     * determine whether the blob has been uploaded to s3 in the past by anyone
     * @param blobId 
     * @returns 
     */
    getIsBlobIdInS3(blobId: string) {
        if (this.uploadeds.length === 0 && this.downloadeds.length === 0) {
            throw Error(`VideoCacheRecord.getBlobIsInS3: No uploadeds or downloadeds ${this._id}`)
        }
        const seqNum = this.seqNum(blobId)
        const i = seqNum - 1
        if (this.uploadeds.length > 0) {
            return this.uploadeds[i]
        } else {
            return this.downloadeds[i]
        }
    }

    // All blobs for this video have been downloaded from S3
    get downloaded() {
        return this.downloadeds.length && this.downloadeds.every(d => d)
    }

    // All blobs for this locally generated video have been uploaded to S3
    get uploaded() {
        return this.uploadeds.length > 0 && this.uploadeds.every(d => d)
    }

    /** 
     * should check if blob is already in s3 if any of the vcr blobs are shared in the local team storage (lan) OR
     * if we are in electron context and can't determine whether or not it's in the local team storage
     */
    async shouldCheckIfBlobIsInS3(blobId: string) {
        if (!getHasElectronContext()) return false
        if (this.uploadeds.length === 0) return false
        const blobLanShareds = VideoCacheRecord.sharedLanBlobs.get(this._id)
        if (!blobLanShareds) {
            if (isSlttAppStorageEnabled()) {
                const { response, videoBlob } = await this.tryLoadBlobFromLan(blobId)
                if (response) {
                    return videoBlob !== null
                }
            }
            console.warn(`### VideoCacheRecord.shouldCheckIfBlobIsInS3: blobLanShareds is undefined for ${this._id}`)
            return true
        } else {
            return blobLanShareds.length > 0 && blobLanShareds.some(b => b)
        }
    }

    /** All blobs that are safe to delete from the cache
     * @param treatRecentAccessDateAsSafe (false by default) - when true it will treat recently accessed 
     * blobs as safe to delete
    */
    getSafeToDeletes({ treatRecentAccessDateAsSafe = HOTFIX_UNBLOCK_CACHE_CLEAN_RECENT_BLOBS }: { treatRecentAccessDateAsSafe?: boolean } = {}) {
        const { totalBlobs } = this
        if (this.uploadeds.length === 0 && this.downloadeds.length === 0) {
            // no blobs cached, so can't delete any
            return Array(totalBlobs).fill(false)
        }
        if (isSlttAppStorageEnabled()) {
            if (this.accessDate === INHERITED_UPLOAD_ACCESS_DATE) {
                // blobs can be gotten again from local team storage, so okay to delete them all
                return this.uploadeds.map(() => true)
            }
            const blobLanShareds = VideoCacheRecord.sharedLanBlobs.get(this._id)
            if (blobLanShareds && blobLanShareds.length > 0 && blobLanShareds.every(b => b)) {
                // blobs can be gotten again from local team storage, so okay to delete them all
                return Array(totalBlobs).fill(true)
            }
        }
        if (!treatRecentAccessDateAsSafe) {
            // if accessed within the last hour, don't delete in case it's needed
            const dateAccessed = this.accessDate && new Date(convertFromCanonicalTimestamp(this.accessDate))
            if (dateAccessed && dateAccessed.getTime() > Date.now() - 60 * 60 * 1000) {
                return Array(totalBlobs).fill(false)
            }
        }
        if (this.uploadeds.length > 0) {
            // We only allow deleting vcrs for uploads if they are all uploaded
            // otherwise it's impossible (or need more code) to determine which 
            // are still in the cache and which are not
            const areAllUploaded = this.uploaded
            return Array(totalBlobs).fill(areAllUploaded)
        }
        if (this.downloadeds.length > 0) {
            return [...this.downloadeds]
        }
        console.error(`### Unexpected state: uploadeds.length=${this.uploadeds.length}, downloadeds.length=${this.downloadeds.length}`)
        return Array(totalBlobs).fill(false)
    }

    /** I don't think we need getSafeToDeleteBlobsSize, just use vcr.size which we ensure is set */

    /**
     * add up the sizes of the blobs that ARE safe to delete
     * anticipate each vcr has a list of equally sized blobs (except for the last one)
     */
    // getSafeToDeleteBlobsSize(warnOnceVcrSizeBlockSizeMismatch: (msg: string) => void) {
    //     const safeToDeletesCopy = [...this.getSafeToDeletes()]
    //     if (safeToDeletesCopy.length === 0 || safeToDeletesCopy.filter(b => b).length === 0) return 0
    //     const [isLastBlobSafeToDelete, ...remainingSafeToDeletes] = [...safeToDeletesCopy].reverse()
    //     /** need to account for partial downloads since vcr.size is added per blob fetch */
    //     const blobsInVcrSize = this.uploadeds.length /* all uploadeds */ ||
    //         /* assume downloaded blobs fill from beginning to end, 
    //          * so only one missing blob should be [true, true, false].
    //          * assume we delete from beginning to end
    //          * so deleting the first would look like [false, true, false]
    //          * in that case, we can find the first 'true' from the end
    //          * and subtract that from totalBlobs to get the number of blobs that
    //          * contributed to this.size
    //          */
    //         this.totalBlobs - [...this.downloadeds].reverse().findIndex(d => d === true)
    //     let lastBlobSize = (this.size % BLOB_BLOCK_SIZE) || BLOB_BLOCK_SIZE
    //     let blockSize = BLOB_BLOCK_SIZE
    //     const expectedTotalSizeForStandardBlockSize = ((blobsInVcrSize - 1) * blockSize) + lastBlobSize
    //     if (this.size !== expectedTotalSizeForStandardBlockSize) {
    //         if (this.totalBlobs === 1) {
    //             const greaterOrLess = this.size > BLOB_BLOCK_SIZE ? 'greater' : 'less'
    //             warnOnceVcrSizeBlockSizeMismatch(`vcr (${this._id}) size (${this.size}) is ${greaterOrLess} than BLOB_BLOCK_SIZE (${BLOB_BLOCK_SIZE}) bytes`)
    //         } else {
    //             const greaterOrLess = this.size > expectedTotalSizeForStandardBlockSize ? 'greater' : 'less'
    //             warnOnceVcrSizeBlockSizeMismatch(`vcr (${this._id}) size (${this.size}) is ${greaterOrLess} than expected vcr size (${expectedTotalSizeForStandardBlockSize}) assuming BLOB_BLOCK_SIZE (${BLOB_BLOCK_SIZE}) bytes`)
    //         }
    //         // sometimes block sizes are different than we expect (e.g 3.0 mb instead of 4.0 mb)
    //         // an average block size will tend to under-estimate the actual block size. 
    //         // For instance totalBlobs = 2, this.size = 3.620 mb, actual blob sizes [3.0 mb, 0.620 mb], avg block size = 1.810 mb
    //         // That's okay since:
    //         // 1) if we delete them all it will be equal to this.size
    //         // 2) if we under-estimate the size, the cache clean logic will expect to need to delete more vcrs.
    //         // On the contrary, if we over-estimate the size we are able to free up, we may estimate we have enough
    //         // to free up bytesToFree, when in fact we don't
    //         // Furthermore, this.size may not reflect the same size as total possible size reflected in this.totalBlobs
    //         // since can be added up as each blob is downloaded (eg. search for "this.size += blob.size)
    //         // so we need to figure out how many blobs contributed to this.size until something may have interrupted
    //         // blob fetch downloading.
    //         blockSize = this.size / blobsInVcrSize
    //         lastBlobSize = blockSize // since we used an average, the last blob size is new block size 
    //     }
    //     let safeToDeleteSize = remainingSafeToDeletes.filter(b => b).length * blockSize
    //     if (isLastBlobSafeToDelete) {
    //         safeToDeleteSize += lastBlobSize
    //     }

    //     return safeToDeleteSize
    // }

    /**
     * The pending upload video was shared with this client via local team storage
     */
    get isInheritedUpload() {
        return this.uploadeds.length > 0 && this.accessDate === INHERITED_UPLOAD_ACCESS_DATE
    }

    // Some blobs for this VCR need uploaded
    get needsUploaded() {
        return this.uploadeds.length > 0 && !this.uploadeds.every(d => d)
    }

    get uploadCount() {
        return this.uploadeds.filter(d => d === false).length
    }

    // If video did not originate on this computer, this is partly a guess
    get numberUploaded() {
        return this.uploadedBlobs.filter(Boolean).length
    }

    /** exception: might also be `isInheritedUpload` if gotten from local team storage */
    get isLocallyCreatedVideo() {
        return this.uploadeds.length > 0
    }

    get numberDownloaded() {
        // If locally created they are by definition already downloaded
        if (this.isLocallyCreatedVideo) return this.totalBlobs

        return this.downloadeds.filter(Boolean).length
    }

    get totalBlobs() {
        let { _id } = this
        // _id is similar to: TESTnm/200829_201205/210519_153942/210519_153943-9
        // The -9 is the blob count.

        try {
            return parseInt(_id.slice(_id.lastIndexOf('-')+1))
        } catch (err) {
            throw Error('Video _id missing blob count ' + _id)
        }
    }

    // Id without the project and sequence number
    get docId() {
        let { _id } = this
        return _id.slice(_id.indexOf('/') + 1, _id.lastIndexOf('-'))
    }

    findPortion(rt: Root) {
        return rt.project.findPortion(this.docId)
    }

    findPassage(rt: Root) {
        return rt.project.findPassage(this.docId)
    }

    findNote(rt: Root) {
        return this.findPassage(rt)?.findNote(this.docId)
    }

    findVideo(rt: Root) {
        return this.findPassage(rt)?.findVideo(this.docId)
    }

    /**
     * Setup to download the s3 items for this video, if this has not already been done
     */
    async setupDownload() {
        if (!this.downloadeds || !this.downloadeds.length) {
            this.downloadeds = new Array(this.seqNum(this._id)).fill(false)
            await this.saveToDB()
        }      
    }

    /** Set size based on actual blobs present in cache */
    async setSize() {
        if (this.downloadeds.length > 0) {
            for (let i = 0; i < this.downloadeds.length; i++) {
                const blobId = this.videoBlobId(i)
                const videoBlob = new VideoBlob(blobId)
                await videoBlob.loadFromDB()
                if (videoBlob.blob) {
                    this.size += videoBlob.blob.size
                    this.downloadeds[i] = true
                } else {
                    this.downloadeds[i] = false
                }
            }
        } else {
            for (let i = 0; i < this.uploadeds.length; i++) {
                const blobId = this.videoBlobId(i)
                const videoBlob = new VideoBlob(blobId)
                await videoBlob.loadFromDB()
                if (videoBlob.blob) {
                    this.size += videoBlob.blob.size
                } else {
                    console.error(`setSize: blob not found for ${blobId}`)
                }
            }
        }

        const { _id, size } = this

        log(`setSize[${_id}] = ${size}`)

        if (this.size) {
            // Only send a beacon once per hour to avoid spamming the server
            doOnceWhenBackOnline('fixSizeOfVideoCacheRecord',
                () => sendBeacon('Fixed size of VideoCacheRecord', { _id, size }, 60 * 60 * 1000 /* 1 hr debounce */)
            )
            VideoCache.videoCacheDownloader.lastFixedSizeOfVideoCacheRecordTimestamp = Date.now()
        }

        await this.saveToDB()
    }

     /**
     * Access this cache record. This will make it less likely
     * that it is deleted from cache when more space is needed.
     */
    async touch() {
        this.accessDate = _LevelupDB.getDate()
        await this.saveToDB()
    }

    async resetError() {
        if (this.error) {
            this.error = ''
            await this.saveToDB()
        }
    }

    async setError(_id: string, error: string) {
        log(`setError ${error}, ${_id}`)

        this.error = error ?? '*** error message missing ***'
        await this.saveToDB()
    }

    async setInvalidSeqNumError(_id: string) {
        this.error = `BUG: Invalid sequence number ${_id}`
        await this.saveToDB()
    }

    async tryLoadBlobFromLan(blobId: string) {
        if (!isSlttAppStorageEnabled()) return { response: null, videoBlob: null }

        const response = await retrieveBlob({ blobId, vcrTotalBlobs: this.totalBlobs }, 'tryLoadBlobFromDisk') || null
        if (!response) return { response: null, videoBlob: null }
        if (response.blob === null) {
            // blob not found in local team storage
            this.setSharedLanBlob(blobId, false)
            return { response, videoBlob: null }
        }
        const { blob, isUploaded } = response
        let videoBlob = new VideoBlob(blobId, blob)
        await videoBlob.saveToDB()
        this.size += blob.size
        this.setSharedLanBlob(blobId, true)
        await this.setCachedBlobIsInS3(blobId, isUploaded)
        await this.saveToDB()
        return { response, videoBlob} 
    }

    checkBlobIndex(iBlob: number) {
        if (iBlob < 0 || iBlob >= this.totalBlobs || Number.isNaN(iBlob)) {
            throw Error(`VideoCacheRecord.checkBlobIndex: Invalid blob index ${iBlob} for ${this._id}`)
        }
    }

    /**
     * For a cached blob, set the uploadeds[iBlob] if we're in uploaded tracking mode
     * - if isInS3 is `false` or has any uploadeds, then we are in uploaded tracking mode,
     *    - set uploadeds[iBlob] to `isInS3`
     * - if not tracking uploads, at least set downloadeds[iBlob] true to indicate the blob is in the cache
     * @param blobId 
     * @param isInS3 - `false` if the blob isPendingUpload.
     * This will result in setting `false` in the uploadeds array.
     */
    async setCachedBlobIsInS3(blobId: string, isInS3: boolean) {
        if (!getHasElectronContext()) return
        const shouldTrackUploads = !isInS3 || this.uploadeds.length > 0
        const iBlob = this.seqNum(blobId) - 1
        this.checkBlobIndex(iBlob)
        if (shouldTrackUploads) {
            if (this.uploadeds.length === 0) {
                // this.uploaded.length === 0 typically means downloaded from remote server
                // it could also mean it's blobs have been deleted from the cache.
                // The closest thing to that is to first assume all blobs are uploaded
                // until told otherwise 
                this.uploadeds = new Array(this.totalBlobs).fill(true)
            }
            const originalValue = this.uploadeds[iBlob] === true
            if (originalValue === isInS3) return
            this.uploadeds[iBlob] = isInS3
            await this.saveToDB()
            !isInS3 && VideoCache.videoCacheUploader.postMessage({ func: 'upload', _id: this._id })
        } else {
            // at least indicate that the blob is in the cache
            if (this.downloadeds.length === 0) {
                this.downloadeds = new Array(this.totalBlobs).fill(false)
            }
            this.downloadeds[iBlob] = true
            await this.saveToDB()
        }
    }

    /** make downloadeds empty if we already have uploadeds 
     * WARNING: Don't do this in the context of a download in progress
     * since it could lead to canceled download or redundant download
    */
    normalizeDownloadeds() {
        if (this.uploadeds.length > 0 && this.downloadeds.length > 0) {
            this.downloadeds = []
        }
    }

    async syncFromLanBlobInfo(blobId: string) {
        if (!isSlttAppStorageEnabled()) return null
        const blobInfo = await retrieveBlobInfo({ blobId, vcrTotalBlobs: this.totalBlobs }, 'tryRetrieveBlobInfo')
        if (blobInfo) {
            this.setSharedLanBlob(blobId, true)
            await this.setCachedBlobIsInS3(blobId, blobInfo.isUploaded)
        }
        return blobInfo
    }

    async storeBlobsNeedingUploadToDisk() {
        if (!isSlttAppStorageEnabled()) return
        if (this.uploadeds.length === 0 || this.uploaded) return
        const { uploadeds } = this
        // save all to disk before uploading to ensure they all get stored
        await this.resetError()
        const authorizedStorageProjects = new Set(await getAuthorizedStorageProjects())
        if (!authorizedStorageProjects.has(this.projectName())) {
            // if the project is not authorized to store blobs, skip writing to disk
            return
        }
        for (let i = 0; i < uploadeds.length; ++i) {
            if (uploadeds[i]) continue
            const blobId = this.videoBlobId(i)
            if (VideoCacheRecord.sharedLanBlobs.get(this._id)?.[i]) {
                // update upload status if someone else has uploaded it
                await this.syncFromLanBlobInfo(blobId)
                continue // already stored
            }

            // copied/refactored from this.uploadOneBlobToS3()
            // TODO: check if this has since been stored in lan 
            const seqNum = await this.tryGetSeqNum(blobId)
            if (Number.isNaN(seqNum)) throw Error(`Invalid sequence number ${blobId}`)
            let videoBlob = await this.tryLoadVideoBlob(blobId, false) // might set error
            if (!videoBlob?.blob) {
                // try load from disk first. maybe the video cache got cleared?
                const { videoBlob: videoBlob2 } = await this.tryLoadBlobFromLan(blobId)
                videoBlob = videoBlob2
                if (videoBlob?.blob) {
                    continue // already stored. No need to store again
                }
            }
            if (!videoBlob?.blob) throw Error(`No blob found in cache to store ${blobId}`)
            const result = await storeBlob({ blobId, blob: videoBlob.blob!, isUploaded: false, vcrTotalBlobs: this.totalBlobs }, 'storeBlobsNeedingUploadToDisk')
            if (result) {
                this.uploadeds[i] = result.isUploaded // might already be uploaded!
                this.setSharedLanBlob(blobId, true)
            }
        }
        this.saveToDB()
    }

    /**
     * 1) start by uploading the last blob (if not uploaded)
     * in order to indicate in s3 how many total blobs are expected
     * and to possibly provide webm duration information
     * 2) to reduce number of clients that may try to
     * upload the same blobs at the same time:
     * - shuffle the rest not isUploaded
     */
    getIndicesToUpload() {
        const indicesToUpload = []
        const iLast = this.uploadeds.length - 1
        if (this.uploadeds[iLast] === false) {
            indicesToUpload.push(iLast)
        }
        const restToUpload = []
        for (let i = 0; i < iLast; ++i) {
            if (this.uploadeds[i] === false) {
                restToUpload.push(i)
            }
        }
        // shuffle the rest
        indicesToUpload.push(...shuffle(restToUpload))
        return indicesToUpload
    }

    // should not throw due to network errors
    async uploadToServer(decrementUploadCount: () => void) {
        await this.resetError()

        const indicesToUpload = this.getIndicesToUpload()
        for (let i of indicesToUpload) {
            let successful = await this.uploadOneBlobToS3(this.videoBlobId(i)) // should not throw due to network errors
            if (successful) {
                decrementUploadCount()
                if (isSlttAppStorageEnabled()) {
                    await updateBlobUploadedStatus({ blobId: this.videoBlobId(i), isUploaded: true, vcrTotalBlobs: this.totalBlobs }, 'uploadToServer')
                }
            } else {
                break
            }
        }
    }

    async saveUploadStartTime() {
        this.uploadStartTimeMs = Date.now()  // current time in ms
        await this.saveToDB()
    }

    async saveUploadFinishTime() {
        this.uploadFinishTimeMs = Date.now()  // current time in ms
        await this.saveToDB()
    }

    async saveUploadRequest() {
        await this.saveToDB()
    }

    /**
     * Side-effect saves error to vcr and returns Number.NaN
     * @param blobId
     * @returns seqNum: number | Number.NaN
     */
    async tryGetSeqNum(blobId: string) {
        let seqNum = this.seqNum(blobId)
        if (seqNum === -1) {
            await this.setInvalidSeqNumError(blobId)
            return Number.NaN
        }
        return seqNum
    }

    /**
     * Side-effect: If error: saves error to vcr and returns null
     * @param blobId 
     * @returns VideoBlob | null
     */
    async tryLoadVideoBlob(blobId: string, mustBePresent = true) {
        const videoBlob = new VideoBlob(blobId)
        try {
            await videoBlob.loadFromDB(mustBePresent)
            return videoBlob
        } catch (error) {
            this.error = `BUG: loadFromDB failed ${blobId}`
            this.uploadeds = []
            await this.saveToDB()
            return null
        }
    } 

    /* ===== Uploader Routines ===== */
    // Upload a single cached blob to server
    // Return true if successful.
    // Network related error should not cause a throw.
    async uploadOneBlobToS3(blobId: string) {
        log(`uploadToS3 start [${blobId}]`)

        let projectName = this.projectName()
        let seqNum = await this.tryGetSeqNum(blobId)
        if (Number.isNaN(seqNum)) return false

        let videoBlob = await this.tryLoadVideoBlob(blobId)
        if (!videoBlob) return false

        const shouldCheckIsBlobInS3 = await this.shouldCheckIfBlobIsInS3(blobId)

        let retryLimit = 5
        while (retryLimit > 0) {
            try {
                await API.pushBlob(projectName, VideoCacheRecord.baseUrl(blobId), seqNum, videoBlob.blob!, shouldCheckIsBlobInS3)
                log(`uploadToS3 done [${blobId}]`)
                this.uploadeds[seqNum - 1] = true
                await this.resetError()
                await this.saveToDB()
                return true
            } catch (error) {
                log(`uploadToS3 error`, error)
                await this.setError(blobId, (error as Error).message)
            } finally {
                --retryLimit
            }
        }

        return false
    }

    uploadRate() {
        let { uploadStartTimeMs, uploadFinishTimeMs, size } = this
        if (uploadStartTimeMs === -1 || uploadFinishTimeMs === -1) {
            return 0
        }
        return (size / 1024) / ((uploadFinishTimeMs - uploadStartTimeMs + 1) / 1000)    // KB/s
    }

    /* ===== Downloader Routines ===== */

    /* Fetch the j'th (0 index) blob for this videos.
     * Return emoty string on success, otherwise error message.
     * Set downloadResults[j] to status from enum VideoDownloadResult.
     * Sets downloads[j] = true on success.
     * Do NOT throw an exception
    **/
    async fetchBlob(j: number, reportProgress: IReportProgress): Promise<VideoDownloadResult> {
        let result: VideoDownloadResult

        // You can test handling of missing (not yet uploaded) blob by entering this is the console
        //    localStorage.forcedDownloadResult='missing'   [refresh window]
        // Temporarily remove forced download response
        //    window.VideoCacheRecord.forcedDownloadResult=''
        // Permanently remove forced download response
        //    localStorage.forcedDownloadResult=''    [refresh window]
        // Similarly for 'error' and 'failed'
        let { forcedDownloadResult } = VideoCacheRecord
        if (forcedDownloadResult !== VideoDownloadResult.NONE) {
            let jStart = parseInt(localStorage.getItem('forcedDownloadResultJ') ?? '0')
            if (j >= jStart) {
                log('fetchBlob forcedDownloadResult', `${this._id}[${j + 1}]`)
                this.downloadResults[j] = forcedDownloadResult
                return forcedDownloadResult
            }
        }

        try {
            result = await this._fetchBlob(j, reportProgress)
            log('fetchBlob result', `${this._id}[${j + 1}]`)
        } catch (error) {
            systemError(error)
            result = VideoDownloadResult.FAILED
        }

        this.downloadResults[j] = result
        return result
    }

    async _fetchBlob(j: number, reportProgress: IReportProgress): Promise<VideoDownloadResult> {
        let url = this.videoBlobId(j)
        log('_fetchBlob', url)

        let vb = new VideoBlob(url)
        await vb.loadFromDB()
        if (vb.blob) {
            // it appears another client already downloaded this
            // it's possible that the vcr has already been updated as well
            this.downloadeds[j] = true
            await this.saveToDB() // WARNING: this may overwrite the other client's state :(
            // TODO: should we make another vcr to loadFromDB() and inherit the download* state from it
            // if it's `accessDate` timestamp is more recent than ours?
            return VideoDownloadResult.SUCCESS
        }
        const responseFromRetrieveBlob = isSlttAppStorageEnabled() &&
            await retrieveBlob({ blobId: url, vcrTotalBlobs: this.totalBlobs }, '_fetchBlob') || null
        let blob = responseFromRetrieveBlob?.blob || new Blob()
        if (!responseFromRetrieveBlob) {
            // await delayUntilOnline('_fetchBlob', 1000)
            let [error1, signedUrl] = await VideoCacheRecord.signedUrlFactory.getSignedUrl(this.projectName(), url)
            if (error1) return VideoDownloadResult.ERROR
            try {
                blob = await fetchBlob2(signedUrl, reportProgress)
            } catch (error) {
                return VideoDownloadResult.ERROR
            }
        }

        let videoBlob = new VideoBlob(url, blob)
        await videoBlob.saveToDB()

        this.size += blob.size
        this.downloadeds[j] = true
        const isInS3 = !responseFromRetrieveBlob /* downloaded */ ||
            responseFromRetrieveBlob.isUploaded /* gotten from local team storage */
        if (getHasElectronContext()) {
            this.setSharedLanBlob(url, !!responseFromRetrieveBlob)
            await this.setCachedBlobIsInS3(url, isInS3)
        }
        await this.saveToDB()
        if (isSlttAppStorageEnabled() && !responseFromRetrieveBlob) {
            const authorizedStorageProjects = new Set(await getAuthorizedStorageProjects())
            if (authorizedStorageProjects.has(this.projectName())) {
                const result = await storeBlob({ blobId: url, blob, isUploaded: true, vcrTotalBlobs: this.totalBlobs }, '_fetchBlob')
                if (result) {
                    if (this.uploadeds.length > 0) {
                        // Not sure why we'd be trying to download something
                        // that has an upload status. Can't hurt to debug.
                        debugger
                        // since we're in the context of a download from s3
                        // make sure our uploadeds reflect this.
                        this.uploadeds[j] = true
                    }
                    this.setSharedLanBlob(url, true)
                    await this.saveToDB()
                }
            }
        }
        return VideoDownloadResult.SUCCESS
    }

    async setDownloadMessage(message: string) {
        debug(`setDownloadMessage ${message}`)

        this.downloadMessage = message
        // Dont do this, causes too many DB writes and we don't really need to persist
        //await this.saveToDB()
    }

    // Extract the sequence number (-n) at end of id
    seqNum(_id?: string) {
        let id = _id || this._id
        let i = id.lastIndexOf('-')
        if (i === -1) {
            console.error(`!!!seqNum: no seqnum in _id [${id}]`)
            return -1
        }
        return parseInt(id.slice(i + 1))
    }

    // Get the id without the sequence number
    static baseUrl(_id: string) {
        let i = _id.lastIndexOf('-')
        if (i === -1) {
            throw Error(`no seqnum in _id [${_id}]`)
        }
        return _id.slice(0, i)
    }

    /** Return the id of the i'th blob (0 origin) */
    videoBlobId(i: number) { 
        return `${VideoCacheRecord.baseUrl(this._id)}-${i + 1}` 
    }

    projectName() { return this._id.split('/')[0] }

    /** remove stuff we don't want stored in the db */
    toSanitized(): PropertiesOnly<VideoCacheRecord> {
        const serialized = safeStableStringify(this)
        const sanitized = JSON.parse(serialized)
        return sanitized
    }

    async saveToDB(tag?: string) { 
        try {
            const sanitized = this.toSanitized()
            tag && log(tag, JSON.stringify(sanitized, null, 4))
            await VideoCache._db.put(VideoCache.CACHEDVIDEOS, sanitized)
            VideoCacheRecord.updateCache(this)
        } catch (error) {
            if (error instanceof Error) {
                this.error = error.message
            } else {
                // not sure what does `throw null` but it has happened
                // see https://app.rollbar.com/a/biblesocieties/fix/item/SLTT/977
                // and https://app.rollbar.com/a/biblesocieties/fix/item/SLTT/540
                this.error = `Unknown Error: ${JSON.stringify(error)}`
            }
        }
    }

    /**
     * @return {boolean} true iff a record with this id is already present in DB
     */
    async loadFromDB(tag?: string) {
        let doc = await VideoCache._db.get(VideoCache.CACHEDVIDEOS, this._id)
        if (doc) {
            tag && log(`loadFromDB [${tag}] ${this._id}`, doc)
            this.clone(doc)
        } else {
            tag && log(`loadFromDB ${this._id} [not present yet]`)
            return false
        }

        return true
    }

    /** Delete cached blobs and reset state of cache entry to not downloaded.
      This frees up space in our disk quota. */
    async deleteBlobs({ treatRecentAccessDateAsSafe = HOTFIX_UNBLOCK_CACHE_CLEAN_RECENT_BLOBS }: { treatRecentAccessDateAsSafe?: boolean } = {}) {
        let { _id } = this
        const safeToDeletes = this.getSafeToDeletes({ treatRecentAccessDateAsSafe })
        const deletedBlobs = Array(this.totalBlobs).fill(false)
        for (let i = 0; i < this.seqNum(_id); ++i) {
            if (!safeToDeletes[i]) {
                // This blob is not safe to delete
                continue
            }
            await VideoCache._db.delete(VideoCache.VIDEOBLOBS, this.videoBlobId(i))
            deletedBlobs[i] = true
            if (this.downloadeds.length > 0) {
                // indicate blob has been removed from cache
                this.downloadeds[i] = false
            }
        }
        if (deletedBlobs.some(b => b)) {
            if (this.uploaded) {
                this.normalizeDownloadeds()
                this.uploadeds = []
            } else if (this.downloadeds.length && this.downloadeds.every(d => !d)) {
                this.downloadeds = []
            }
            await this.saveToDB()
        }
    }

    /** Add a blob to be uploaded and save blob in database */
    async addBlob(blob: Blob) {
        this.uploadeds.push(false)
        this.size += blob.size

        let id = this.videoBlobId(this.uploadeds.length-1)
        debug('addBlob', id, blob.size)

        let videoBlob = new VideoBlob(id, blob)
        await videoBlob.saveToDB()
    }

    /**
     * Load a blob from the local team storage (lan) if it is present
     * @param iBlob - zero-based index of the blob to load
     */
    async loadVideoBlobFromLan(iBlob: number) {
        if (!isSlttAppStorageEnabled()) return null
        const blobId = this.videoBlobId(iBlob)
        const { videoBlob } = await this.tryLoadBlobFromLan(blobId)
        return videoBlob
    }

    /** Read and concatenate individual blobs to create complete video blob
     * SIDE-EFFECT: touches the video cache record
     * SIDE-EFFECT: checks if (previously) pending upload blobs have since been uploaded 
    */
    async getVideoBlob(_type?: string): Promise<Blob | null> {
        let { _id } = this
        let blobs: Blob[] = []

        await this.touch()

        let seqNum = this.seqNum(_id)
        if (seqNum <= 0) throw Error(`no seq number ${_id}`)

        let failed = false

        for (let i = 0; i < seqNum; ++i) {
            const blobId = this.videoBlobId(i)
            let blobInfo: Awaited<ReturnType<VideoCacheRecord['syncFromLanBlobInfo']>> = null
            if (isSlttAppStorageEnabled() && this.uploadeds[i] === false) {
                // since we are possibly sharing the blob with other lan clients, 
                // see if the uploadeds status for this blob has changed
                // since the last time we checked
                blobInfo = await this.syncFromLanBlobInfo(blobId)
            }
            let videoBlob: VideoBlob | null = new VideoBlob(blobId)
            await videoBlob.loadFromDB(false)
            if (videoBlob?.blob) {
                blobs.push(videoBlob.blob)
            } else {
                if (isSlttAppStorageEnabled() && (blobInfo || this.uploadeds[i] !== false)) {
                    videoBlob = await this.loadVideoBlobFromLan(i)
                    if (videoBlob?.blob) {
                        blobs.push(videoBlob.blob)
                    }
                }
                if (!videoBlob?.blob) {
                    // Something has gone wrong, blob is not present in cache
                    this.downloadeds[i] = false
                    failed = true
                }            
            }
        }

        if (failed) {
            await this.saveToDB('### must re-download video blob: ' + this._id)
            return null
        }

        if (blobs.length === 1) {
            return blobs[0]
        }

        const combinedBlob = await mergeBlobs(blobs, _type || 'video/mp4')
        return combinedBlob
    }

    // ---- Utilities for unit testing ----

    async delete() {
        await this.deleteBlobs()
        if (!this.hasBeenCached) {
            await VideoCache._db.delete(VideoCache.CACHEDVIDEOS, this._id)
        }
    }
}

/**
 * This is a workaround for a problem introduced in Chrome 133. See
 * https://issues.chromium.org/issues/392376370#comment16.
 * This doesn't appear to be necessary when the blobs aren't coming from IndexedDB.
 * (shared from AVTT)
 **/
const mergeBlobs = async (blobs: Blob[], mimeType?: string) => {
    const buffers = await Promise.all(blobs.map((blob) => blob.arrayBuffer()))
    const totalLength = buffers.reduce((sum, buf) => sum + buf.byteLength, 0)
    const mergedBuffer = new Uint8Array(totalLength)

    let offset = 0
    buffers.forEach((buf) => {
        mergedBuffer.set(new Uint8Array(buf), offset)
        offset += buf.byteLength

    })

    return new Blob([mergedBuffer], { type: mimeType || blobs[0].type })
}

// For cypress testing, write a file with a name like '__video1.mp4' to cache

async function _writeTestFileToCache_(file: File) {
    let _id = file.name.slice(2, -4) + '-1'
    log('writeTestFileToCache', _id)

    let vcr = await VideoCacheRecord.get(_id)
    await vcr.deleteBlobs() // if existing video data for this file, remove it
    vcr.size = file.size
    await vcr.addBlob(file)

    log('!!!writeTestFileToCache DONE', _id)
}

let _window: any = window
_window._writeTestFileToCache_ = _writeTestFileToCache_
_window.VideoCacheRecord = VideoCacheRecord

// console.log(await window.VideoCacheRecord.get('TESTnm/200829_201205/210519_194355/210519_194356-9'))


